import {
    keepPreviousData,
    useMutationState,
    useQueries,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { ApiError } from "@jobenvoy/core";
import { useToast } from "@jobenvoy/ui";
import { describeError } from "@/lib/api/client";
import {
    getApplication,
    getApplicationEvents,
    getApplicationFit,
    getEnvoy,
    getEnvoyOverview,
    getNeedsYou,
    getQueue,
    getQueueHistory,
    type QueuePageParams,
    getReady,
    listApplications,
    markApplied,
    pauseEnvoy,
    queuePosting,
    saveEnvoySettings,
    skipApplication,
    skipPosting,
    unpauseEnvoy,
    unqueuePosting,
    type Application,
    type ApplicationList,
    type NeedsYouList,
    type Queue,
    type ReadyList,
    type Envoy,
    type EnvoySettings,
    type ListApplicationsParams,
    type PauseReason,
    type SkipReason,
} from "@/lib/api/envoy";
import { track } from "@/lib/analytics";
import type { TailorChoices } from "@/lib/posting/tailorChoices";
import { isClosedRefusal, POSTING_CLOSED } from "@/lib/tools/tailorAnswer";
import { tailorStage } from "@/lib/applications";
import type { CompanyRef } from "@/lib/jobs/company";
import {
    applicationsOnTab,
    sameTrackedList,
    totalsParams,
    type TrackedStatus,
} from "@/lib/jobs/tracked";
import { batches, mergeRecords } from "@/lib/batches";
import { onceEach } from "./catalogue";
import { browserZone, keys } from "./keys";
import { useBaselineNow } from "./resume";
import { useResumeFirst } from "./resumeFirstContext";
import { ONE_AT_A_TIME, useSingleFlightMutation } from "./singleFlight";

export function useEnvoyOverview(timeZone: string = browserZone()) {
    return useQuery({
        queryKey: keys.envoy.overview(timeZone),
        queryFn: () => getEnvoyOverview(timeZone, 14),
    });
}

/** The last `days` days of activity, today the last, for a period's counts. Waits for `days`. */
export function useEnvoyDays(days: number | null, timeZone: string = browserZone()) {
    return useQuery({
        queryKey: keys.envoy.overviewDays(timeZone, days ?? 0),
        queryFn: () => getEnvoyOverview(timeZone, days ?? 1),
        enabled: days !== null,
    });
}

// Each entry once, however often envoy names it: one row, one React key.
const queueOnce = (queue: Queue): Queue => ({
    ...queue,
    entries: onceEach(queue.entries, (entry) => entry.posting_id),
});
const readyOnce = (list: ReadyList): ReadyList => ({
    ...list,
    applications: onceEach(list.applications, (entry) => entry.application.application_id),
});
const needsYouOnce = (list: NeedsYouList): NeedsYouList => ({
    ...list,
    applications: onceEach(list.applications, (entry) => entry.application.application_id),
});
const applicationsOnce = (list: ApplicationList): ApplicationList => ({
    ...list,
    applications: onceEach(list.applications, (application) => application.application_id),
});

export function useQueue(params?: QueuePageParams) {
    return useQuery({
        queryKey: params ? [...keys.envoy.queue, params] : keys.envoy.queue,
        queryFn: () => getQueue(params),
        select: queueOnce,
        refetchInterval: 15_000,
    });
}
export function useQueueHistory(params: QueuePageParams) {
    return useQuery({
        queryKey: ["envoy", "queue-history", params],
        queryFn: () => getQueueHistory(params),
        refetchInterval: 15_000,
    });
}

export function useReady(params?: QueuePageParams) {
    return useQuery({
        queryKey: params ? [...keys.envoy.ready, params] : keys.envoy.ready,
        queryFn: () => getReady(params?.limit, params?.cursor),
        select: readyOnce,
        refetchInterval: params ? 15_000 : false,
    });
}

export function useNeedsYou(params?: QueuePageParams) {
    return useQuery({
        queryKey: params ? [...keys.envoy.needsYou, params] : keys.envoy.needsYou,
        queryFn: () => getNeedsYou(params?.limit, params?.cursor),
        select: needsYouOnce,
        refetchInterval: params ? 15_000 : false,
    });
}

/**
 * The two decisions a person makes on an application: it went in, or it
 * is skipped with a reason. Either refetches everything envoy answers,
 * since one application moving changes the counts, the queue and the
 * lists alike.
 */
export function useApplicationActions() {
    const client = useQueryClient();
    const toast = useToast();
    const settle = () => client.invalidateQueries({ queryKey: keys.envoy.all });

    const sent = useSingleFlightMutation({
        mutationFn: ({
            applicationId,
            resumeId,
        }: {
            applicationId: string;
            resumeId?: string | null;
        }) => markApplied(applicationId, resumeId),
        onSuccess: (application) => {
            track({ name: "application_marked_sent", application_id: application.application_id });
            toast.success("Recorded as sent", `${application.title} at ${application.company}`);
            return settle();
        },
        onError: (error) => toast.error("Not recorded", describeError(error)),
    });

    const skip = useSingleFlightMutation({
        mutationFn: ({ applicationId, reason }: { applicationId: string; reason: SkipReason }) =>
            skipApplication(applicationId, reason),
        onSuccess: (application, { reason }) => {
            track({
                name: "application_skipped",
                application_id: application.application_id,
                reason,
            });
            toast.success("Skipped", `${application.title} at ${application.company}`);
            return settle();
        },
        onError: (error) => toast.error("Not skipped", describeError(error)),
    });

    return { sent, skip };
}

/** Hand a posting to the envoy, or take one back out before its tailor starts. */
const QUEUE_ADD = ["envoy", "queue", "add"] as const;

/** A posting to queue, with what the person chose for its resume, when they were asked. */
export interface QueueAsk {
    postingId: string;
    choices?: TailorChoices;
}

/** One queuing of a posting at a time, whatever was chosen for it. */
const queuedPosting = ({ postingId }: QueueAsk): string => postingId;
const QUEUE_REMOVE = ["envoy", "queue", "remove"] as const;

/** Envoy will not queue this now: no saved resume yet, or the posting has closed. */
export function isNotQueueable(error: unknown): boolean {
    return error instanceof ApiError && error.status === 412;
}

/** Every posting being added to the queue now, from any button. */
function useAddingIds(): string[] {
    return useMutationState({
        filters: { mutationKey: QUEUE_ADD, status: "pending" },
        select: (mutation) => (mutation.state.variables as QueueAsk).postingId,
    });
}

/** Whether a posting is being added to the queue now, from any button. */
export function useAdding(postingId: string): boolean {
    return useAddingIds().includes(postingId);
}

export function useQueueActions() {
    const client = useQueryClient();
    const toast = useToast();
    const needResume = useResumeFirst();
    const { refetch: readBaseline } = useBaselineNow();
    const settle = () => client.invalidateQueries({ queryKey: keys.envoy.all });

    const add = useSingleFlightMutation(
        {
            mutationKey: QUEUE_ADD,
            mutationFn: ({ postingId, choices }: QueueAsk) =>
                choices ? queuePosting(postingId, choices) : queuePosting(postingId),
            onSuccess: (entry) => {
                track({ name: "posting_queued", posting_id: entry.posting_id });
                toast.success("Added to your queue", entry.application.title);
                // The tailor it starts spends one, so the tailors pill is asked again.
                void client.invalidateQueries({ queryKey: keys.billing.all });
                return settle();
            },
            // A 429 is said on the card itself: nothing was queued or charged.
            onError: async (error, ask) => {
                if (error instanceof ApiError && error.status === 429) return;
                if (isClosedRefusal(error)) {
                    toast.error("Not added", POSTING_CLOSED);
                    return;
                }
                if (needResume && isNotQueueable(error)) {
                    const read = await readBaseline();
                    if (!read.isError && !read.data) return needResume(ask);
                }
                toast.error("Not added", describeError(error));
            },
        },
        queuedPosting,
    );

    const remove = useSingleFlightMutation({
        mutationKey: QUEUE_REMOVE,
        mutationFn: (postingId: string) => unqueuePosting(postingId),
        onSuccess: (entry) => {
            track({ name: "posting_unqueued", posting_id: entry.posting_id });
            toast.success("Taken out of your queue", entry.application.title);
            return settle();
        },
        onError: (error) => toast.error("Not taken out", describeError(error)),
    });

    // Every posting being added or taken out, not only the last one asked.
    const adding = useAddingIds();
    const removing = useMutationState({
        filters: { mutationKey: QUEUE_REMOVE, status: "pending" },
        select: (mutation) => mutation.state.variables as string,
    });

    return {
        add,
        remove,
        adding,
        removing,
        /** Queues a posting, once however often it is pressed. Rejects as the route does. */
        queueOnce: (postingId: string): Promise<unknown> => add.mutateAsync({ postingId }),
        /** Takes a posting out, once however often it is pressed. Rejects as the route does. */
        unqueueOnce: (postingId: string): Promise<unknown> => remove.mutateAsync(postingId),
    };
}

/** The person's envoy, or null until its settings are saved once (envoy answers 404). */
export function useEnvoy() {
    return useQuery({
        queryKey: keys.envoy.settings,
        queryFn: async (): Promise<Envoy | null> => {
            try {
                return await getEnvoy();
            } catch (error) {
                if (error instanceof ApiError && error.status === 404) return null;
                throw error;
            }
        },
    });
}

/** The most postings one read of applications names: `MAX_POSTING_IDS` in the api crate. */
export const MAX_POSTING_IDS = 200;

/** The person's applications to these postings, keyed by posting, 200 a read. */
export function useApplicationsFor(postingIds: readonly string[]) {
    return useQueries({
        queries: batches(postingIds, MAX_POSTING_IDS).map((ids) => ({
            queryKey: keys.envoy.applicationsFor(ids),
            queryFn: async (): Promise<Record<string, Application>> => {
                const list = await listApplications({ postingIds: ids, limit: ids.length });
                return Object.fromEntries(
                    list.applications.map((application) => [application.posting_id, application]),
                );
            },
            // Asked again while one is being tailored, so it turns Tailored on its own.
            refetchInterval: ({ state }: { state: { data?: Record<string, Application> } }) =>
                Object.values(state.data ?? {}).some(
                    (application) => tailorStage(application) === "tailoring",
                )
                    ? 15_000
                    : false,
        })),
        combine: mergeRecords<Application>,
    });
}

/**
 * Saves the settings whole. Every failure is left to the caller: the
 * dialog reads a refusal back onto the form, and says anything else in
 * its footer.
 */
export function useSaveEnvoy() {
    const client = useQueryClient();
    return useSingleFlightMutation(
        {
            mutationFn: ({ settings }: { settings: EnvoySettings; first: boolean }) =>
                saveEnvoySettings(settings),
            onSuccess: (envoy, { first }) => {
                track({ name: "envoy_saved", first });
                client.setQueryData(keys.envoy.settings, envoy);
                return client.invalidateQueries({ queryKey: keys.envoy.all });
            },
        },
        ONE_AT_A_TIME,
    );
}

/** Put the envoy to rest with a reason, or wake it. */
export function usePauseActions() {
    const client = useQueryClient();
    const toast = useToast();
    const settle = (envoy: Envoy) => {
        client.setQueryData(keys.envoy.settings, envoy);
        return client.invalidateQueries({ queryKey: keys.envoy.all });
    };

    const pause = useSingleFlightMutation(
        {
            mutationFn: (reason: PauseReason) => pauseEnvoy(reason),
            onSuccess: (envoy, reason) => {
                track({ name: "envoy_paused", reason });
                toast.success(
                    "Your envoy is resting",
                    "Everything it found, and every record, stays.",
                );
                return settle(envoy);
            },
            onError: (error) => toast.error("Not paused", describeError(error)),
        },
        ONE_AT_A_TIME,
    );

    const resume = useSingleFlightMutation(
        {
            mutationFn: () => unpauseEnvoy(),
            onSuccess: (envoy) => {
                track({ name: "envoy_resumed" });
                toast.success("Your envoy is looking again");
                return settle(envoy);
            },
            onError: (error) => toast.error("Not resumed", describeError(error)),
        },
        ONE_AT_A_TIME,
    );

    return { pause, resume };
}

/** Skip a posting by the catalogue's id, with or without an application to it yet. */
export function useSkipPosting() {
    const client = useQueryClient();
    const toast = useToast();
    return useSingleFlightMutation({
        mutationFn: ({ postingId, reason }: { postingId: string; reason: SkipReason }) =>
            skipPosting(postingId, reason),
        onSuccess: (application, { reason }) => {
            track({
                name: "application_skipped",
                application_id: application.application_id,
                reason,
            });
            toast.success("Skipped", `${application.title} at ${application.company}`);
            return client.invalidateQueries({ queryKey: keys.envoy.all });
        },
        onError: (error) => toast.error("Not skipped", describeError(error)),
    });
}

const applicationOf = (postingId: string | null) => ({
    queryKey: keys.envoy.applicationOf(postingId ?? ""),
    queryFn: async (): Promise<Application | null> => {
        const list = await listApplications({ postingIds: [postingId as string], limit: 1 });
        return list.applications[0] ?? null;
    },
    enabled: Boolean(postingId),
});

/** The person's application to one posting, or null when there is none. */
export function useApplicationOf(postingId: string | null) {
    return useQuery(applicationOf(postingId));
}

/** How often a tailor being written is asked after, and for how long. */
const TAILOR_WATCH_EVERY_MS = 3_000;
export const TAILOR_WATCH_MS = 3 * 60_000;

/**
 * The resume a tailor ended with, or null. A FAILED row never carries a
 * resume, and one being written has none yet; the check stays as a guard.
 */
export function writtenResume(application: Application | null | undefined): string | null {
    if (application?.status === "FAILED" || application?.status === "TAILORING") return null;
    return application?.resume_id ?? null;
}

/**
 * The application to one posting, asked after every three seconds until
 * its resume is written or its tailor has failed. It stops after three
 * minutes. A tab left in the background is still asking: a tailor is what
 * a person leaves a tab to wait for.
 */
export function useTailoredApplication(postingId: string | null) {
    return useQuery({
        ...applicationOf(postingId),
        refetchIntervalInBackground: true,
        refetchInterval: ({ state }) => {
            if (writtenResume(state.data) || state.data?.status === "FAILED") return false;
            const asked = (state.dataUpdateCount + state.errorUpdateCount) * TAILOR_WATCH_EVERY_MS;
            return asked < TAILOR_WATCH_MS ? TAILOR_WATCH_EVERY_MS : false;
        },
    });
}

/** One application by its id. */
export function useApplication(applicationId: string | null) {
    return useQuery({
        queryKey: keys.envoy.application(applicationId ?? ""),
        queryFn: () => getApplication(applicationId as string),
        enabled: Boolean(applicationId),
    });
}

/** The fit the envoy scored when it matched the posting. */
export function useApplicationFit(applicationId: string | null) {
    return useQuery({
        queryKey: keys.envoy.fit(applicationId ?? ""),
        queryFn: () => getApplicationFit(applicationId as string),
        enabled: Boolean(applicationId),
    });
}

/** The application's record, in order. */
export function useApplicationEvents(applicationId: string | null) {
    return useQuery({
        queryKey: keys.envoy.events(applicationId ?? ""),
        queryFn: async () => (await getApplicationEvents(applicationId as string)).events,
        enabled: Boolean(applicationId),
    });
}

/** One page of the person's applications, the one changed last first. */
export function useApplicationsPage(params: ListApplicationsParams) {
    return useQuery({
        queryKey: keys.envoy.history(params),
        queryFn: (): Promise<ApplicationList> => listApplications(params),
        select: applicationsOnce,
        placeholderData: keepPreviousData,
    });
}

/**
 * A page of the person's applications for a Jobs tab, while `params` names
 * one, held to what belongs on `tab`, and to `company` on one company's
 * page, whatever envoy sends; envoy's total stands as sent. The tab's last
 * page stays while its next is read; another tab's never does.
 */
export function useTrackedApplications(
    params: ListApplicationsParams | null,
    tab: TrackedStatus | null,
    company?: CompanyRef,
) {
    return useQuery({
        queryKey: keys.envoy.tracked(params),
        queryFn: (): Promise<ApplicationList> => listApplications(params as ListApplicationsParams),
        select: (list: ApplicationList): ApplicationList => {
            const once = applicationsOnce(list);
            return tab === null
                ? once
                : { ...once, applications: applicationsOnTab(once.applications, tab, company) };
        },
        enabled: params !== null,
        placeholderData: (previous, previousQuery) =>
            sameTrackedList(
                previousQuery?.queryKey[3] as ListApplicationsParams | null | undefined,
                params,
            )
                ? previous
                : undefined,
    });
}

/**
 * How many applications are in the person's queue and how many went in,
 * over every page, each from a list of one, at `company` on one company's
 * page. Asked only while `enabled`.
 */
export function useTrackedTotals(
    enabled: boolean,
    company?: CompanyRef,
): {
    queue: number | undefined;
    applied: number | undefined;
} {
    return useQueries({
        queries: totalsParams(company).map((params) => {
            return {
                queryKey: keys.envoy.tracked(params),
                // Null, not undefined, for an envoy that gives no total: a
                // query's data cannot be undefined.
                queryFn: async (): Promise<number | null> =>
                    (await listApplications(params)).total ?? null,
                enabled,
            };
        }),
        combine: ([queue, applied]) => ({
            queue: queue?.data ?? undefined,
            applied: applied?.data ?? undefined,
        }),
    });
}
